import Cookies from 'js-cookie';

const appId = process.env.REACT_APP_MTID_APP_ID || '';
const authHostUrl = (process.env.REACT_APP_MTID_HOST_URL || 'https://mtid-prod.mthreads.com').replace(/\/+$/, '');
// 认证接口统一走当前站点的反向代理，避免浏览器跨域请求。
const authApiBaseUrl = '';

const normalizeUrlPath = (value: string) => {
  const url = new URL(value);
  url.pathname = url.pathname.replace(/\/{2,}/g, '/');
  return url.toString();
};

const buildAuthUrl = (path: string) =>
  normalizeUrlPath(`${authHostUrl}/${[appId, path].filter(Boolean).join('/')}`);

export interface CaptchaData {
  captchaId: string;
  imageBase64: string;
  codeLength: number;
}

export const getLoginCaptcha = async (): Promise<CaptchaData> => {
  const response = await fetch(`${authApiBaseUrl}/api/v1/auth/login/captcha`);
  if (!response.ok) {
    throw new Error(`获取图形验证码失败：${response.status}`);
  }

  const result = await response.json();
  if (!result?.data?.captchaId || !result?.data?.imageBase64) {
    throw new Error(result?.message || '获取图形验证码失败');
  }
  return {
    captchaId: String(result.data.captchaId),
    imageBase64: String(result.data.imageBase64).replace(/^data:image\/[^;]+;base64,/, ''),
    codeLength: Number(result.data.codeLength) || 4,
  };
};

export const hashPasswordForTransport = async (password: string) => {
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(password));
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
};

type DeactivateVerification =
  | { verifyType: 'password'; password: string }
  | { verifyType: 'phone'; phoneCode: string }
  | { verifyType: 'email'; emailCode: string };

export const deactivateAccount = async (
  captchaId: string,
  captchaCode: string,
  verification: DeactivateVerification,
) => {
  const token = Cookies.get('access_token');
  if (!token) throw new Error('登录状态已失效，请重新登录');

  const verificationPayload = verification.verifyType === 'password'
    ? {
      verifyType: verification.verifyType,
      password: await hashPasswordForTransport(verification.password),
    }
    : verification;
  const authorization = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  const response = await fetch(`${authApiBaseUrl}/api/v1/account/deactivate`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: authorization,
    },
    body: JSON.stringify({
      captchaId,
      captchaCode,
      ...verificationPayload,
    }),
  });
  const result = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(result?.message || `账号注销失败：${response.status}`);
  }
  return result;
};

export const sendDeactivateEmailCode = async (): Promise<void> => {
  const token = Cookies.get('access_token');
  if (!token) throw new Error('登录状态已失效，请重新登录');

  const authorization = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  const response = await fetch(`${authApiBaseUrl}/api/v1/account/lifecycle/email/send-code`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: authorization,
    },
    body: '{}',
  });

  const result = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(result?.message || '发送邮箱验证码失败');
  }
};

export const sendDeactivatePhoneCode = async (): Promise<void> => {
  const token = Cookies.get('access_token');
  if (!token) throw new Error('登录状态已失效，请重新登录');

  const authorization = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  const response = await fetch(`${authApiBaseUrl}/api/v1/account/lifecycle/phone/send-code`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: authorization,
    },
    body: '{}',
  });

  const result = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(result?.message || '发送手机验证码失败');
  }
};

export interface UserIdentity {
  phone: string | null;
  email: string | null;
}

export const getUserIdentity = async (): Promise<UserIdentity> => {
  const token = Cookies.get('access_token');
  if (!token) throw new Error('登录状态已失效，请重新登录');

  const authorization = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  const response = await fetch(`${authApiBaseUrl}/api/v1/identity/me`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: authorization,
    },
  });

  const result = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(result?.message || '获取用户信息失败');
  }

  // 返回结果结构: { data: { phone: "...", email: "...", ... }, traceId: "..." }
  return {
    phone: result?.data?.phone ?? null,
    email: result?.data?.email ?? null,
  };
};

// 未单独配置入口时，按 MTID 的应用 OIDC 入口动态拼接 App ID。
const loginUrl = normalizeUrlPath(process.env.REACT_APP_MTID_LOGIN_URL || buildAuthUrl('oidc/auth'));

export const redirectUrl = process.env.REACT_APP_MTID_REDIRECT_URL || window.location.origin;

const appendQueryParameters = (baseUrl: string, params: Record<string, string>) => {
  const url = new URL(baseUrl);
  Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));
  return url.toString();
};

export const redirectToLogin = () => {
  const state = `login_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  const redirectPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  sessionStorage.setItem(`auth_redirect_${state}`, redirectPath);

  const url = appendQueryParameters(loginUrl, {
    client_id: appId,
    response_type: 'id_token token',
    redirect_uri: redirectUrl,
    scope: 'openid profile username phone',
    state,
    nonce: state,
  });
  window.location.replace(url);
};

export const consumeAuthCallback = () => {
  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return '';

  const params = new URLSearchParams(hash);
  const accessToken = params.get('access_token');
  if (!accessToken) return '';

  const expiresIn = Number(params.get('expires_in'));
  const expires = Number.isFinite(expiresIn) && expiresIn > 0 ? expiresIn / 86400 : undefined;
  Cookies.set('access_token', accessToken, expires ? { expires } : undefined);
  const idToken = params.get('id_token');
  if (idToken) Cookies.set('id_token', idToken, expires ? { expires } : undefined);

  const state = params.get('state');
  const redirectPath = state ? sessionStorage.getItem(`auth_redirect_${state}`) || '' : '';
  if (state) sessionStorage.removeItem(`auth_redirect_${state}`);
  window.history.replaceState(null, document.title, `${window.location.pathname}${window.location.search}`);
  return redirectPath && redirectPath.startsWith('/') ? redirectPath : '';
};

export const loadUserProfile = async () => {
  const token = Cookies.get('access_token');
  if (!token || !appId) return;
  try {
    const response = await fetch(buildAuthUrl('api/v3/get-profile?withCustomData=true&withIdentities=true&withDepartmentIds=true'), {
      headers: { authorization: token },
    });
    if (!response.ok) return;
    const profile = await response.json();
    const data = profile?.data || profile;
    const name = data?.username || data?.phone || data?.email || '';
    if (name) Cookies.set('name', name);
    if (data?.customData) Cookies.set('teenageModeStatus', JSON.stringify(data.customData));
  } catch {
    // A profile request must not prevent the token-based login from completing.
  }
};

export const logout = () => {
  const idToken = Cookies.get('id_token');
  Cookies.remove('name');
  Cookies.remove('avatar');
  Cookies.remove('access_token');
  Cookies.remove('id_token');
  Cookies.remove('teenageModeStatus');
  const url = appendQueryParameters(buildAuthUrl('oidc/session/end'), {
    ...(idToken ? { id_token_hint: idToken } : {}),
    post_logout_redirect_uri: redirectUrl,
  });
  window.location.reload();
};
